// API utility functions for tenant-aware API calls
// Provides consistent header management across all services

/**
 * Get tenant domain for API calls
 * If VITE_TENANT_DOMAIN env var is set, always use it.
 * Otherwise, use the actual hostname.
 */
export const getTenantDomain = (): string => {
  // If env var is set, always use it
  const envTenantDomain = import.meta.env.VITE_TENANT_DOMAIN
  if (envTenantDomain) {
    return envTenantDomain
  }

  // Otherwise use the actual hostname
  if (typeof window !== 'undefined') {
    return window.location.hostname
  }

  return 'monutrack.app'
}

/**
 * Get impersonated employee ID from localStorage
 * Used to inject X-Impersonate-Employee header for admin impersonation
 */
const getImpersonatedEmployeeId = (): string | null => {
  if (typeof window === 'undefined') return null
  return localStorage.getItem('impersonation-employee-id')
}

/**
 * Create standard headers for API calls with tenant domain
 * Does NOT include impersonation headers - use createImpersonatedApiHeaders for that
 */
export const createApiHeaders = (additionalHeaders: Record<string, string> = {}): Record<string, string> => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Tenant-Domain': getTenantDomain(),
    'Content-Type': 'application/json',
    ...additionalHeaders
  }

  return headers
};

/**
 * Create headers for workforce and monumenta API calls with impersonation support
 * Automatically injects X-Impersonate-Employee header if impersonation is active
 * Use this for /workforce/* and /monumenta/* endpoints only
 */
export const createImpersonatedApiHeaders = (additionalHeaders: Record<string, string> = {}): Record<string, string> => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Tenant-Domain': getTenantDomain(),
    'Content-Type': 'application/json',
    ...additionalHeaders
  }

  // Inject impersonation header if active
  const impersonatedEmployeeId = getImpersonatedEmployeeId()
  if (impersonatedEmployeeId) {
    headers['X-Impersonate-Employee'] = impersonatedEmployeeId
  }

  return headers
};

/**
 * Enhanced logging for tenant-aware API calls
 */
export const logApiCall = (path: string, tenantDomain?: string): void => {
  // API call logging available via browser dev tools network tab
};
